import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import { STEP_TYPE_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { Title } from '../../../Shared'
import { BaseEditorContainer, EditorContentCardContainer } from '../../styles'
import Field from 'components/Shared/Fields'
import BaseDivider from '../../BaseDivider'
import ExtraTextEditor from '../../ExtraTextEditor'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaign } from 'state/campaigns'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { useNewTranslation } from 'hooks'
import Toggle from 'particles/Toggle'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { FormikTextListGroup } from 'particles/TextInputs/TextList/Group'

interface SubmitProofPhotoProps {
    taskType: TASK_TYPE_ENUM
    stepType: STEP_TYPE_ENUM
    stepNumber: number
    requiredFieldNames: string[]
    uuid?: string
    isIncluded: boolean
    toggleIncludedStep: (stepType: STEP_TYPE_ENUM, isToggled: boolean) => void
    showBrief?: boolean
}

const SubmitProofPhoto: React.FC<SubmitProofPhotoProps> = ({
    taskType,
    stepType,
    stepNumber,
    requiredFieldNames,
    isIncluded,
    toggleIncludedStep,
    uuid,
    showBrief,
}) => {
    const editingCampaign = useSelector(selectEditingCampaign)
    const campaignStatus = editingCampaign ? editingCampaign.status : undefined
    const [t] = useNewTranslation()
    const viewType = useContext(ViewTypeContext)
    const isCampaignDraftOrForEdit =
        campaignStatus === CAMPAIGN_STATUS_ENUM.DRAFT || campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_EDIT
    const handleToggle = (): void => {
        const updateValue = !isIncluded
        toggleIncludedStep(stepType, updateValue)
    }

    const { proofText, proofTextSubtitle } = useMemo(() => {
        let titleKey = '',
            subtitleKey = ''
        switch (taskType) {
            case TASK_TYPE_ENUM.PURCHASE_PRODUCT:
                titleKey = 'campaignEditor.createTask.turnOnProofPurchaseHeader'
                subtitleKey = 'campaignEditor.createTask.turnOnProofPurchaseSubtext'
                break
            case TASK_TYPE_ENUM.ATTEND_EVENT:
                titleKey = 'campaignEditor.createTask.turnOnProofAttendanceHeader'
                subtitleKey = 'campaignEditor.createTask.turnOnProofAttendanceSubtext'
                break
        }

        return {
            proofText: t(titleKey),
            proofTextSubtitle: t(subtitleKey),
        }
    }, [t, taskType])

    return (
        <BaseEditorContainer id={uuid && uuid}>
            <EditorContentCardContainer>
                <Title taskType={taskType} stepType={stepType} stepNumber={stepNumber} />
                <CampaignEditorInputFieldContainer>
                    <Toggle
                        isChecked={isIncluded}
                        handleChange={handleToggle}
                        isDisabled={
                            viewType === EDITOR_ROUTE_ENUM.VIEW ||
                            (campaignStatus !== undefined &&
                                ![
                                    CAMPAIGN_STATUS_ENUM.DRAFT,
                                    CAMPAIGN_STATUS_ENUM.FOR_EDIT,
                                    CAMPAIGN_STATUS_ENUM.FOR_REVIEW,
                                ].includes(campaignStatus))
                        }
                        isReadOnly={showBrief || !isCampaignDraftOrForEdit}
                        label={proofText}
                        subtext={proofTextSubtitle}
                        identifier={`${uuid}_${
                            taskType === TASK_TYPE_ENUM.ATTEND_EVENT
                                ? fieldIdentifiers.attendEventToggle
                                : fieldIdentifiers.purchaseProductToggle
                        }`}
                    />
                </CampaignEditorInputFieldContainer>
                <BaseDivider />
                {isIncluded ? (
                    <CampaignEditorInputFieldContainer>
                        <Field.TextInputField
                            className={'input-area'}
                            label={t('campaignEditor.createTask.enterStepTitleHeader')}
                            placeholder={t('placeholderText.enterStepTitleSubmitProofPhoto')}
                            name={`stepData.${stepType}.data.title`}
                            isRequired={requiredFieldNames.includes('title')}
                            width={'100%'}
                            identifier={`${uuid}_${fieldIdentifiers.stepTitle}`}
                            isReadOnly={showBrief}
                        />
                        <BaseDivider />
                        <FormikTextListGroup
                            title={t('campaignEditor.createTask.enterInstructionsHeader')}
                            subtitle={t('campaignEditor.createTask.enterProofPurchaseInstructionsSubtext')}
                            placeholder={t('placeholderText.enterInstructionBulletPoint')}
                            buttonText={t('button.addBulletPoint')}
                            isRequired={requiredFieldNames.includes('how_to_buy')}
                            name={`stepData.${stepType}.data.things_to_note`}
                            width={'100%'}
                            identifier={`${uuid}_${fieldIdentifiers.thingsToNote}`}
                            isReadOnly={showBrief}
                        />
                        <BaseDivider />
                        {!showBrief ? (
                            <ExtraTextEditor name={`stepData.${stepType}.data.extra_data`} isRequired />
                        ) : (
                            <></>
                        )}
                    </CampaignEditorInputFieldContainer>
                ) : null}
            </EditorContentCardContainer>
        </BaseEditorContainer>
    )
}

export default SubmitProofPhoto
